package com.schoolconnect.app.feature.documents

import com.schoolconnect.app.datasource.network.response.DocumentResponseDTO
import com.schoolconnect.app.datasource.network.response.ClassSectionResponse

data class DocumentLibraryLookupOption(
    val code: String,
    val label: String
)

data class DocumentLibraryClassOption(
    val id: String,
    val label: String
)

internal fun ClassSectionResponse.toDocumentLibraryClassOption(): DocumentLibraryClassOption? {
    val sectionId = id?.trim().orEmpty()
    val className = className?.trim().orEmpty()
    val sectionName = sectionName?.trim().orEmpty()
    if (sectionId.isBlank() || className.isBlank()) return null
    return DocumentLibraryClassOption(
        id = sectionId,
        label = if (sectionName.isBlank()) className else "$className - $sectionName"
    )
}

data class DocumentLibraryDraft(
    val title: String,
    val description: String,
    val category: String,
    val applicableTo: String,
    val filename: String,
    val contentType: String,
    val bytes: ByteArray,
    val classTargetIds: List<String> = emptyList()
)

data class DocumentLibraryRow(
    val id: String,
    val fileId: String = "",
    val title: String,
    val category: String,
    val applicableTo: String,
    val fileType: String,
    val updatedOn: String,
    val description: String = "—",
    val fileName: String = "—",
    val createdBy: String = "—",
    val createdOn: String = "—",
    val updatedBy: String = "—",
    val fileSizeLabel: String = "—",
    val documentId: String = ""
)

enum class DocumentLibrarySortField(val apiField: String) {
    DOCUMENT_ID("doc_id"),
    TITLE("title"),
    CATEGORY("category"),
    APPLICABLE_TO("applicable_to"),
    FILE_TYPE("file_type"),
    UPDATED_ON("updated_at")
}

internal fun DocumentResponseDTO.toDocumentLibraryRow(): DocumentLibraryRow = DocumentLibraryRow(
    id = docId.orEmpty().ifBlank { id.orEmpty() },
    fileId = fileId.orEmpty(),
    documentId = id.orEmpty(),
    title = title.orEmpty().ifBlank { "—" },
    category = documentCategoryLabel(category),
    applicableTo = documentAudienceLabel(applicableTo),
    fileType = fileType.orEmpty().ifBlank { fileName.orEmpty().substringAfterLast('.', "—").uppercase().ifBlank { "—" } },
    updatedOn = documentDateLabel(updatedAt),
    description = description.orEmpty().ifBlank { "No description provided" },
    fileName = fileName.orEmpty().ifBlank { "—" },
    createdBy = createdByName.orEmpty().ifBlank { "—" },
    createdOn = documentDateLabel(createdAt),
    updatedBy = updatedByName.orEmpty().ifBlank { "—" },
    fileSizeLabel = fileSizeBytes?.let(::documentFileSizeLabel) ?: "—"
)

private fun documentDateLabel(value: String?): String {
    val parts = value.orEmpty().take(10).split('-')
    if (parts.size != 3) return value.orEmpty().ifBlank { "—" }
    val month = parts[1].toIntOrNull()
    val day = parts[2].toIntOrNull()
    if (parts[0].length != 4 || month == null || day == null || month !in 1..12 || day !in 1..31) return value.orEmpty().ifBlank { "—" }
    val monthName = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")[month - 1]
    return "${day.toString().padStart(2, '0')} $monthName ${parts[0]}"
}

internal fun documentCategoryLabel(value: String?): String = when (value?.trim()?.uppercase()) {
    "POLICIES" -> "Policies"
    "GUIDELINES" -> "Guidelines"
    "FORMS" -> "Forms"
    "HANDBOOKS" -> "Handbooks"
    else -> value?.trim().orEmpty().ifBlank { "Other" }
}

internal fun documentAudienceLabel(value: String?): String = when (value?.trim()?.uppercase()) {
    "ALL_USERS" -> "All Users"
    "PARENTS" -> "Parents"
    "TEACHERS" -> "Teachers"
    "SELECTED_CLASSES" -> "Classes"
    else -> value?.trim().orEmpty().ifBlank { "All Users" }
}

private fun documentFileSizeLabel(bytes: Int): String = when {
    bytes >= 1024 * 1024 -> "${(bytes / (1024.0 * 1024.0) * 10).toLong() / 10.0} MB"
    bytes >= 1024 -> "${(bytes / 1024.0 * 10).toLong() / 10.0} KB"
    else -> "$bytes B"
}
